import { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { Box, Button, Link, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import api from "../../services/axios-client";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({ password: "", confirmPassword: "" });
  const [isTokenValid, setIsTokenValid] = useState<number>(0);
  const [message, setMessage] = useState<string>("");

  const navigate = useNavigate();
  // This hook reads the 'token' from the URL, e.g., /reset-password/some-unique-token
  const { token } = useParams<{ token: string }>();

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const result = await api.get(
        `${process.env.REACT_APP_API_URL}/verify-reset-password-token/${token}`
      );
      if (result.status === 200) {
        setIsTokenValid(result.status);
      }
    } catch (error: any) {
      setIsTokenValid(error.status);
      if (error.status === 404 || error.status === 400) {
        setMessage(error.response.data.error);
      } else {
        setMessage(error.message);
      }
    }
  };

  const validate = () => {
    const newErrors = { password: "", confirmPassword: "" };
    let isValid = true;

    // Password validation
    if (!password) {
      newErrors.password = "Password is required.";
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long.";
      isValid = false;
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password = "Password must contain at least one capital letter.";
      isValid = false;
    } else if (!/\d/.test(password)) {
      newErrors.password = "Password must contain at least one number.";
      isValid = false;
    } else if (/\s/.test(password)) {
      newErrors.password = "Password cannot contain spaces.";
      isValid = false;
    } else if (/[^a-zA-Z0-9@#$_!-]/.test(password)) {
      newErrors.password =
        "Password can only contain letters, numbers, and the special characters @, #, $, -, _, !";
      isValid = false;
    }

    // Confirm Password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirming your password is required.";
      isValid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async(e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const data = { password, token };
    console.log("Submitting new password data:", data);
    
    try {
      const result = await api.post(
        `${process.env.REACT_APP_API_URL}/reset-password`,
        data
      );
      if (result.status === 200) {
       setIsTokenValid(2);
      }
    } catch (error: any) {
      setIsTokenValid(error.status);
      setMessage(error.response.data.error);
    }

  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      {isTokenValid === 0 && <p>Verifying Token. Please wait...</p>}
      {isTokenValid === 200 && (
        <Box
          sx={{
            p: 4,
            maxWidth: 400,
            width: "100%",
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Reset Your Password
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="New Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              error={!!errors.password}
              helperText={errors.password}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setConfirmPassword(e.target.value)
              }
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Reset Password
            </Button>
            <Box sx={{ textAlign: "center" }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Back to Login
              </Link>
            </Box>
          </Box>
        </Box>
      )}

      {/** Invalid or expired token */}

      {isTokenValid !== 200 && isTokenValid !== 0 && isTokenValid !== 2 && 
        <Box
          sx={{
            p: 4,
            maxWidth: 400,
            width: "100%",
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {message}
          </Typography>
          <Box sx={{ textAlign: "center" }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Request another link
            </Link>
          </Box>
        </Box>
      }

{/** Password reset successfull */}

      {isTokenValid === 2 && 
        <Box
          sx={{
            p: 4,
            maxWidth: 400,
            width: "100%",
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Password reset successful!
          </Typography>
          <Box sx={{ textAlign: "center" }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Back to Login
            </Link>
          </Box>
        </Box>
      }

    </Box>
  );
};

export default ResetPassword;
